import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RankPrefix } from 'shared/components/RankPrefix';
import { BATTLE_PURE_MODE_LABELS, CPU_DIFFICULTY_LABELS, type CpuDifficulty } from 'features/battle/index';
import { fetchBattleLeaderboard } from 'features/battle/leaderboard-api';
import { rankBattleBoard, type BattleBoardRow, type BattleLeaderboardProfile } from 'features/battle/leaderboard';
import { BATTLE_ALLOW_UNCLAIMED } from 'features/catalog/rarity-config';
import { talosBadgesFaviconURL } from 'config/constants';
import './BadgesBattleView.css';
import './BattleLeaderboardsView.css';

const TOP_N = 3;
const LEADERBOARD_DIFFICULTIES: readonly CpuDifficulty[] = ['impossible', 'hard', 'easy'];

type Props = {
  ownProfileId: string | null;
  /** Extra ms before the board cascade (boot reveal). Frozen on mount. */
  entranceDelayMs?: number;
};

const enterDelay = (baseMs: number, index: number): string => {
  const cascadeMs = index < 10 ? index * 55 : Math.min(10 * 55 + (index - 10) * 8, 700);
  return `${baseMs + cascadeMs}ms`;
};

const Board: React.FC<{
  title: string;
  difficulty: CpuDifficulty;
  rows: BattleBoardRow[];
  ownProfileId: string | null;
  claimedOnly?: boolean;
  enterDelay?: string;
  onOpen: (profileId: string) => void;
}> = ({ title, difficulty, rows, ownProfileId, claimedOnly, enterDelay: delay, onOpen }) => {
  const podium = Array.from({ length: TOP_N }, (_, index) => rows[index] ?? null);
  const ownIndex = ownProfileId ? rows.findIndex((row) => row.profileId === ownProfileId) : -1;
  const ownRow = ownIndex >= TOP_N ? { row: rows[ownIndex], rank: ownIndex + 1 } : null;
  const claimedTipId = `bb-lb-pure-tip-${difficulty}`;
  return (
    <section
      className={`bb-lb-board is-${difficulty}`}
      style={delay ? ({ '--bb-lb-enter-delay': delay } as React.CSSProperties) : undefined}
    >
      <div className="bb-lb-board-head">
        <h3>{title}</h3>
        {BATTLE_ALLOW_UNCLAIMED && claimedOnly ? (
          <span className="bb-claimed-pill" aria-describedby={claimedTipId}>
            {BATTLE_PURE_MODE_LABELS.pure}
            <span className="bb-deck-pure-tip" id={claimedTipId} role="tooltip">
              Wins from decks built only from claimed badges.
            </span>
          </span>
        ) : null}
      </div>
      <ol>
        {podium.map((row, index) => {
          const rank = index + 1;
          if (!row) {
            return (
              <li key={`open-${rank}`}>
                <div className={`bb-lb-row is-open is-rank-${rank}`}>
                  <RankPrefix rank={rank} />
                  <span className="bb-lb-name">Open</span>
                  <span className="bb-lb-count">—</span>
                </div>
              </li>
            );
          }
          const isYou = row.profileId === ownProfileId;
          return (
            <li key={row.profileId}>
              <button
                type="button"
                className={`bb-lb-row is-rank-${rank}${isYou ? ' is-you' : ''}`}
                onClick={() => onOpen(row.profileId)}
              >
                <RankPrefix rank={rank} />
                <span className="bb-lb-name">
                  {row.name}
                  {isYou ? <em>(you)</em> : null}
                </span>
                <span className="bb-lb-count">{row.count}</span>
              </button>
            </li>
          );
        })}
      </ol>
      {ownRow ? (
        <button type="button" className="bb-lb-row is-pinned is-you" onClick={() => onOpen(ownRow.row.profileId)}>
          <RankPrefix rank={ownRow.rank} />
          <span className="bb-lb-name">
            {ownRow.row.name} <em>(you)</em>
          </span>
          <span className="bb-lb-count">{ownRow.row.count}</span>
        </button>
      ) : null}
    </section>
  );
};

export const BattleLeaderboardsView: React.FC<Props> = ({ ownProfileId, entranceDelayMs = 0 }) => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<BattleLeaderboardProfile[] | null>(null);
  const [error, setError] = useState(false);
  const [claimedOnlyOn, setClaimedOnlyOn] = useState(false);
  const claimedOnly = BATTLE_ALLOW_UNCLAIMED && claimedOnlyOn;
  const [enterBaseMs] = useState(entranceDelayMs);

  useEffect(() => {
    let cancelled = false;
    fetchBattleLeaderboard()
      .then((rows) => {
        if (!cancelled) setProfiles(rows);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const classicBoards = useMemo(() => {
    if (!profiles) return [];
    return LEADERBOARD_DIFFICULTIES.map((difficulty) => ({
      difficulty,
      rows: rankBattleBoard(profiles, { mode: 'classic', difficulty, claimedOnly }),
    }));
  }, [claimedOnly, profiles]);

  const arenaBoards = useMemo(() => {
    if (!profiles) return [];
    return LEADERBOARD_DIFFICULTIES.map((difficulty) => ({
      difficulty,
      rows: rankBattleBoard(profiles, { mode: 'arena', difficulty }),
    }));
  }, [profiles]);

  return (
    <article className="bb-lb">
      <header
        className="bb-lb-head"
        style={{ '--bb-lb-enter-delay': enterDelay(enterBaseMs, 0) } as React.CSSProperties}
      >
        <p className="bb-lb-eyebrow">Battle Badges</p>
        <h1>Leaderboard</h1>
      </header>
      {error ? <p className="bb-lb-empty">Could not load the leaderboard.</p> : null}
      {!profiles && !error ? (
        <div className="bb-lb-loading" role="status" aria-live="polite" aria-label="Loading">
          <img src={talosBadgesFaviconURL} alt="" className="spinning" width={28} height={28} />
        </div>
      ) : null}
      {profiles ? (
        <div className="bb-lb-columns">
          <div>
            <div
              className="bb-lb-col-head"
              style={{ '--bb-lb-enter-delay': enterDelay(enterBaseMs, 1) } as React.CSSProperties}
            >
              <h2>Battle Badges</h2>
              {BATTLE_ALLOW_UNCLAIMED ? (
                <button
                  type="button"
                  className={`bb-claimed-switch${claimedOnly ? ' is-on' : ''}`}
                  aria-pressed={claimedOnly}
                  onClick={() => setClaimedOnlyOn((on) => !on)}
                >
                  <span className="bb-claimed-switch-label">{BATTLE_PURE_MODE_LABELS.pure}</span>
                  <span className="bb-claimed-switch-track" aria-hidden>
                    <span className="bb-claimed-switch-knob" />
                  </span>
                </button>
              ) : null}
            </div>
            {classicBoards.map((board, index) => (
              <Board
                key={board.difficulty}
                title={`Most wins vs ${CPU_DIFFICULTY_LABELS[board.difficulty as CpuDifficulty]} CPU`}
                difficulty={board.difficulty}
                rows={board.rows}
                ownProfileId={ownProfileId}
                claimedOnly={claimedOnly}
                enterDelay={enterDelay(enterBaseMs, 2 + index)}
                onOpen={(id) => navigate(`/${id}`)}
              />
            ))}
          </div>
          <div>
            <div
              className="bb-lb-col-head"
              style={{ '--bb-lb-enter-delay': enterDelay(enterBaseMs, 1) } as React.CSSProperties}
            >
              <h2>Battle Arena</h2>
            </div>
            {arenaBoards.map((board, index) => (
              <Board
                key={board.difficulty}
                title={`Most wins vs ${CPU_DIFFICULTY_LABELS[board.difficulty as CpuDifficulty]} CPU`}
                difficulty={board.difficulty}
                rows={board.rows}
                ownProfileId={ownProfileId}
                enterDelay={enterDelay(enterBaseMs, 2 + index)}
                onOpen={(id) => navigate(`/${id}`)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
};
